import React, { FC } from "react";
import { useTranslation } from "lib/i18n/use-translation";
import { CardSize, CardVariant, ContentCard } from "components/ui";
import { extractItem } from "components/ui/ContentCard";
import { ReactComponent as HeartIcon } from "Assets/icon/heart-icon.svg";
import { ReactComponent as PlayIcon } from "Assets/playIcon.svg";
import { useCardTracking } from "../../hooks/use-card-tracking";

import useFavorite from "../../hooks/use-favourite";
import {
  LearnerMixedContentListItem,
  LearnerRecordingCardPartsFragment,
} from "../../api/types";

import styles from "./styles.module.scss";
import { ContentListType } from "../../pages/home/types";
import { useOpenModalFromCard } from "providers/content-modal-manager";

export type FeaturedLayoutProps = {
  featuredItem?: LearnerRecordingCardPartsFragment;
  secondaryItems?: LearnerRecordingCardPartsFragment[]; // Expects 2 items for the typical layout
  subtitle?: string;
};

type TrackedContentCardProps = {
  item: LearnerRecordingCardPartsFragment;
  listName: string;
  listPosition: number;
  size?: CardSize;
};

const TrackedContentCard: FC<TrackedContentCardProps> = ({
  item,
  listName,
  listPosition,
  size = CardSize.REGULAR,
}) => {
  const { onFavourite, loading: isFavouriteLoading } = useFavorite();
  const { t } = useTranslation(["common"]);

  const openModalFromCard = useOpenModalFromCard();
  const extractedItem = extractItem(item as LearnerMixedContentListItem);
  const { logPlayClick, logFavoriteClick } = useCardTracking({
    item: extractedItem,
    listName,
    listPosition,
    showBiometrics: false,
  });

  return (
    <ContentCard
      item={extractedItem}
      variant={CardVariant.PRIMARY}
      size={size}
      listName={listName}
      listPosition={listPosition}
      actionButtons={[
        {
          icon: <PlayIcon />,
          ariaLabel: t("common:a11y.watchNow"),
          onClick: (e, itemId) => {
            e.preventDefault();
            logPlayClick();
            openModalFromCard({ type: "recording", id: itemId }, true);
          },
        },
        {
          icon: (
            <HeartIcon
              stroke={
                item.isFavourite
                  ? styles["love-red"]
                  : styles["dark-shade-grey"]
              }
              color={item.isFavourite ? styles["love-red"] : "transparent"}
            />
          ),
          ariaLabel: item.isFavourite
            ? t("common:a11y.removeFromFavourites")
            : t("common:a11y.addToFavourites"),
          loading: isFavouriteLoading,
          onClick: (e, itemId) => {
            e.preventDefault();
            logFavoriteClick(item.isFavourite);
            onFavourite({
              id: itemId,
              isFavourite: item.isFavourite,
              __typename: item.__typename,
            });
          },
        },
      ]}
    />
  );
};

const FeaturedLayout: FC<FeaturedLayoutProps> = ({
  featuredItem,
  secondaryItems,
  subtitle,
}) => {
  const { t } = useTranslation(["home"]);

  if (!featuredItem) return null; // no featured item = forYou items array length 0

  return (
    <section aria-labelledby="featured-layout-title">
      <div className={styles.header}>
        <h2 id="featured-layout-title" className={styles.title}>
          {t("home:recommended.title")}
        </h2>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>
      <div className={styles["content-wrapper"]}>
        <div className={styles.mainCardContainer}>
          <TrackedContentCard
            item={featuredItem}
            listName={ContentListType.ForYou}
            listPosition={1}
            size={CardSize.LARGE}
          />
        </div>
        {secondaryItems.length > 0 && (
          <div className={styles["secondary-cards-container"]}>
            {secondaryItems.map((item, index) => (
              <TrackedContentCard
                key={item.id}
                item={item}
                listName={ContentListType.ForYou}
                listPosition={index + 2} // Start from 2 for secondary items
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedLayout;
